import { useState } from "react";
import {
  Eraser,
  KeyRound,
  Link2,
  Lock,
  MonitorSmartphone,
  ScanFace,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Timer,
} from "lucide-react";
import { PLAN_DEFAULTS } from "@sprout/shared/plans";
import { HelpCard, Overline } from "../Layout";
import { useActiveSection, usePage } from "../router";
import { SUPPORT_EMAIL } from "../links";
import { FREE, PAID } from "../plan-format";

const SECTIONS = [
  ["wall-screen", "On the wall screen"],
  ["photos-and-videos", "Photos and videos"],
  ["how-long-proof-stays", "How long proof stays"],
  ["ai", "AI"],
  ["locks-and-sign-in", "Locks and sign-in"],
  ["your-data", "Your data"],
] as const;

const days = PLAN_DEFAULTS.retentionDays;

export default function Privacy() {
  usePage("Privacy & safety · Sprout");
  const [active, setActive] = useState<string>(SECTIONS[0][0]);
  useActiveSection(
    SECTIONS.map(([id]) => id),
    setActive,
  );
  return (
    <>
      <section className="page-head split site-wrap">
        <div>
          <Overline>Privacy &amp; safety</Overline>
          <h1 className="site-h1">Made for kids. Private by default.</h1>
          <p>
            Sprout never decides whether a chore was done. A grown-up does. Here’s exactly what happens to
            your family’s photos and videos.
          </p>
        </div>
        <ul className="privacy-promises">
          <li>
            <ScanFace size={19} /> No facial recognition
          </li>
          <li>
            <Sparkles size={19} /> Photos and videos never go to AI
          </li>
          <li>
            <Lock size={19} /> Media is never public
          </li>
          <li>
            <Timer size={19} /> Proof is deleted after {days} days
          </li>
        </ul>
      </section>

      <section className="page-body site-wrap">
        <nav className="page-toc" aria-label="On this page">
          {SECTIONS.map(([id, label]) => (
            <a key={id} href={`#${id}`} aria-current={active === id ? "true" : undefined}>
              {label}
            </a>
          ))}
        </nav>
        <div className="page-article">
          <section id="wall-screen">
            <h2>On the wall screen</h2>
            <p>
              Children choose who’s playing by tapping their profile. There’s no facial recognition and no
              automatic check of whether a chore was done. Saving a recording isn’t proof on its own. A
              grown-up looks and decides.
            </p>
          </section>
          <section id="photos-and-videos">
            <h2>Photos and videos</h2>
            <p>The camera only records when your child chooses to. After that, every file takes the same four steps.</p>
            <ol className="privacy-steps">
              <li>
                <div>
                  <span className="privacy-icon">
                    <ShieldCheck size={19} />
                  </span>
                  <span className="privacy-num">1</span>
                </div>
                <strong>Checked first</strong>
                <p>Type, size and your plan’s storage are checked before anything uploads.</p>
              </li>
              <li>
                <div>
                  <span className="privacy-icon">
                    <Lock size={19} />
                  </span>
                  <span className="privacy-num">2</span>
                </div>
                <strong>Private storage</strong>
                <p>Files go straight to a private bucket. Nothing is ever public.</p>
              </li>
              <li>
                <div>
                  <span className="privacy-icon">
                    <Eraser size={19} />
                  </span>
                  <span className="privacy-num">3</span>
                </div>
                <strong>Cleaned up</strong>
                <p>Photos lose location and camera details. Proof videos are saved without sound.</p>
              </li>
              <li>
                <div>
                  <span className="privacy-icon">
                    <Link2 size={19} />
                  </span>
                  <span className="privacy-num">4</span>
                </div>
                <strong>Short-lived links</strong>
                <p>Viewing needs parent controls unlocked, and each link expires in ten minutes.</p>
              </li>
            </ol>
          </section>
          <section id="how-long-proof-stays">
            <h2>How long proof stays</h2>
            <p>
              Proof is deleted automatically when your family’s window ends. Progress and points are kept.
              Videos stop at {PLAN_DEFAULTS.videoMaxSeconds} seconds unless you set a shorter limit in Account
              → Privacy.
            </p>
            <dl className="privacy-windows">
              <div>
                <dt className="accent">{days} days</dt>
                <dd>Default for every family</dd>
              </div>
              <div>
                <dt>{FREE.limits.proofRetentionMaxDays} days</dt>
                <dd>Longest on {FREE.name}</dd>
              </div>
              <div>
                <dt>{PAID.limits.proofRetentionMaxDays} days</dt>
                <dd>Longest on {PAID.name}</dd>
              </div>
            </dl>
          </section>
          <section id="ai">
            <h2>AI</h2>
            <p>
              Photos and videos are never sent to AI. AI coaching is a {PAID.name} feature and stays off until
              a grown-up turns on the family’s AI switch. Until then, Buddy uses guided coaching.
            </p>
          </section>
          <section id="locks-and-sign-in">
            <h2>Locks and sign-in</h2>
            <ul className="privacy-locks">
              <li>
                <KeyRound size={19} />
                <span>
                  <strong>A parent PIN guards the tablet.</strong> Grown-up controls unlock for eight hours,
                  then lock again.
                </span>
              </li>
              <li>
                <MonitorSmartphone size={19} />
                <span>
                  <strong>Each wall screen has its own key.</strong> A shared browser can never reach parent
                  tools.
                </span>
              </li>
              <li>
                <Smartphone size={19} />
                <span>
                  <strong>Two-step sign-in.</strong> Use an authenticator app or SMS codes, with recovery
                  codes as backup.
                </span>
              </li>
            </ul>
          </section>
          <section id="your-data">
            <h2>Your data</h2>
            <p>
              Download your family’s records as a file whenever you like, clear proof early, or delete the
              account from Account → Privacy.
            </p>
          </section>
          {SUPPORT_EMAIL && (
            <HelpCard title="A question we didn’t answer?" text="Write to us. A person reads every note." />
          )}
        </div>
      </section>
    </>
  );
}
